import * as yup from "yup";
import {useFetcher} from "react-router-dom";
import {useContext, useEffect} from "react";
import AlertContext from "../../../contexts/AlertContext";
import {FieldArray, Formik, useField, useFormikContext} from "formik";
import moment from "moment-timezone";
import BasicPrimaryButton from "../../../components/Button/BasicPrimaryButton";
import FormikDatePicker from "../../../components/Input/FormikDatePicker";
import MultipleInputHeader from "../../../components/Input/MultipleInputHeader";
import FormikError from "../../../components/Input/FormikError";
import FormikTextInput from "../../../components/Input/FormikTextInput";
import ProductUnitInput from "../../../components/Input/ProductUnitInput";
import FormikMultipleTextInput from "../../../components/Input/FormikMultipleTextInput";
import FormikSelect from "../../../components/Input/FormikSelect";
import FormikSelectWithCross from "../../../components/Input/FormikSelectWithCross";

const productCategorySchema = yup.object({
    id: yup.number().positive().integer(),
    name: yup.string(),
});
const productSchema = yup.object({
    id: yup.number().positive().integer(),
    name: yup.string(),
    defaultUnit: yup.string(),
    category: productCategorySchema,
    retired: yup.boolean(),
});
const treatmentSchema = yup.object({
    product: productSchema
        .required("Product is required"),
    dosage: yup.number().positive().integer()
        .required("Dosage is required"),
    unit: yup.string().trim()
        .required("Unit is required"),
});
const cowStageSchema = yup.object({
    id: yup.number().positive().integer(),
    name: yup.string(),
    retired: yup.boolean(),
});
const proactiveTreatmentsSchema = yup.object({
    id: yup.number().positive().integer(),
    program: yup.string().required(),
    farmUid: yup.string().required(),
    treatmentDate: yup.number().positive().integer()
        .required("Treatment Date is required"),
    cowStage: cowStageSchema
        .required("Cow Stage is required"),
    treatments: yup.array().of(treatmentSchema)
        .required("Treatments are required")
        .min(1, "At least one Treatment is required"),
    cowNumbers: yup.array().of(
        yup.string().required("Cow Number is required")
    )
        .required("Cow Numbers are required")
        .min(1, "At least one Cow Number is required"),
    comments: yup.string().trim()
        .max(511, "Must be 511 characters or less"),
});

export function formDataToProactiveTreatments(formData) {
    const proactiveTreatments = Object.fromEntries(formData);
    proactiveTreatments.cowNumbers = JSON.parse(proactiveTreatments.cowNumbers);
    proactiveTreatments.treatments = JSON.parse(proactiveTreatments.treatments);
    proactiveTreatments.cowStage = JSON.parse(proactiveTreatments.cowStage);

    return proactiveTreatmentsSchema.cast(proactiveTreatments);
}

const CowStageFormikSelect = ({stages, plans, treatmentsFieldName, ...props}) => {
    const [field] = useField(props);
    const {setFieldValue} = useFormikContext();

    useEffect(() => {
        if (field.value) setFieldValue(treatmentsFieldName, plans[field.value.name] || []);
    }, [field.value, plans, setFieldValue, treatmentsFieldName]);

    return <FormikSelect isClearable={false} {...props}/>
}

const ProactiveTreatmentsCreateForm = ({farm, products, stages, plans, ...props}) => {
    const fetcher = useFetcher();
    const {addAlert, handleErrorAlert} = useContext(AlertContext);

    useEffect(() => {
        const result = fetcher.data?.result;
        if (result) addAlert("success", "Submission successful",
            `Added ${result.length} treatment${result.length === 1 ? "" : "s"}`);
        if (fetcher.data?.error) handleErrorAlert(fetcher.data.error, "Submission failed");
    }, [fetcher.data, addAlert, handleErrorAlert]);

    return <Formik
        initialValues={{
            program: "Herd Health Plan",
            farmUid: farm.uid,
            treatmentDate: moment().valueOf(),
            cowStage: null,
            treatments: [{
                product: null,
                dosage: 1,
                unit: "",
            }],
            cowNumbers: [
                "",
            ],
            comments: "",
        }}
        validationSchema={proactiveTreatmentsSchema}
        onSubmit={async values => fetcher.submit({
            ...values,
            treatments: JSON.stringify(values.treatments),
            cowNumbers: JSON.stringify(values.cowNumbers),
            cowStage: JSON.stringify(values.cowStage),
        }, {method: "post"})}
    >
        {formik => <form className="px-4 sm:px-6" method="post" onSubmit={formik.handleSubmit} {...props}>
            <div className="grid grid-cols-12 gap-y-6 gap-x-2 sm:gap-6">
                <div className="col-span-12 sm:col-span-6">
                    <FormikDatePicker label="Treatment Date" name="treatmentDate"/>
                </div>

                <div className="col-span-12 sm:col-span-6">
                    <CowStageFormikSelect
                        name="cowStage" label="Cow Stage" options={stages} optionValue="id" optionLabel="name"
                        stages={stages} plans={plans} treatmentsFieldName="treatments"
                    />
                </div>

                {formik.values.cowStage && <div className="col-span-12">
                    <FieldArray name="treatments">
                        {({push, remove}) =>
                            <div className="grid grid-cols-12 gap-y-6 gap-x-2 sm:gap-6">
                                <div className="col-span-12">
                                    <MultipleInputHeader
                                        header="Treatments"
                                        addOnClick={() => push({
                                            product: null,
                                            dosage: 1,
                                            unit: "",
                                        })}
                                    />
                                    <FormikError name="treatments"/>
                                </div>

                                {formik.values.treatments.map((treatment, index) => [
                                    <div key={`${index}-1`} className="col-span-12 sm:col-span-6">
                                        <FormikSelectWithCross
                                            name={`treatments.${index}.product`} label="Product"
                                            groupKey="category.name"
                                            options={products} optionValue="id" optionLabel="name"
                                            onCrossClick={() => remove(index)}
                                        />
                                    </div>,
                                    <div key={`${index}-2`} className="col-span-6 sm:col-span-3">
                                        <FormikTextInput
                                            label="Dosage" name={`treatments.${index}.dosage`}
                                            type="number" min={1}
                                        />
                                    </div>,
                                    <div key={`${index}-3`} className="col-span-6 sm:col-span-3">
                                        <ProductUnitInput
                                            label="Unit" name={`treatments.${index}.unit`} type="text"
                                            productValue={formik.values.treatments[index].product}
                                        />
                                    </div>,
                                ]).flat()}
                            </div>
                        }
                    </FieldArray>
                </div>}

                {formik.values.cowStage && <div className="col-span-12">
                    <FieldArray name="cowNumbers">
                        {({push, remove}) =>
                            <div className="grid grid-cols-12 gap-y-6 gap-x-2 sm:gap-6">
                                <div className="col-span-12">
                                    <MultipleInputHeader header="Cow Numbers" addOnClick={() => push("")}/>
                                    <FormikError name="cowNumbers"/>
                                </div>

                                {formik.values.cowNumbers.map((cowNumber, index) =>
                                    <div key={index} className="col-span-6 sm:col-span-4">
                                        <FormikMultipleTextInput
                                            label="Cow Number" name={`cowNumbers.${index}`} type="text"
                                            deleteOnClick={() => remove(index)}
                                        />
                                    </div>
                                )}
                            </div>
                        }
                    </FieldArray>
                </div>}

                {formik.values.cowStage && <div className="col-span-12">
                    <FormikTextInput label="Comments" name="comments" as="textarea"/>
                </div>}
            </div>

            <div className="mt-4 py-3">
                <BasicPrimaryButton disabled={fetcher.state === "submitting"} type="submit">
                    Submit
                </BasicPrimaryButton>
            </div>
        </form>}
    </Formik>;
}

export default ProactiveTreatmentsCreateForm;